/**
 * Where a Portal's photo or clip sits in its 9:16 frame.
 *
 * Every surface first centres the picture in the part of the frame its own slab leaves visible
 * (the slab covers the bottom third, and a landscape clip centred in the whole frame ends up
 * half under it). The sender's framing nudges it from there: offsets in percent of the frame,
 * scale in percent (100 = fitted, up to 400 — the composer's pinch). Because the base is each surface's own visible area, a frame set in the
 * composer lands the same way in the viewer on a phone or a desktop.
 *
 *   frame  { x, y, scale } — x/y −100…100 (0 = centred above the slab), scale 100…400
 *   cover  px of the frame's bottom hidden under the slab (0 when there's none)
 */
export const FRAME_DEFAULT = Object.freeze({ x: 0, y: 0, scale: 100 });

export const clampFrame = (f) => ({
  x: Math.max(-100, Math.min(100, Number.isFinite(f?.x) ? f.x : 0)),
  y: Math.max(-100, Math.min(100, Number.isFinite(f?.y) ? f.y : 0)),
  scale: Math.max(100, Math.min(400, Number.isFinite(f?.scale) ? f.scale : 100)),
});

export const isDefaultFrame = (f) => !f
  || (Math.round(f.x || 0) === 0 && Math.round(f.y || 0) === 0 && Math.round(f.scale ?? 100) === 100);

/** CSS transform for the media element, which fills the frame with object-fit: contain. */
export function frameTransform(frame, cover = 0) {
  const f = clampFrame(frame || FRAME_DEFAULT);
  const lift = Math.max(0, cover) / 2;
  return `translate(${f.x}%, calc(${f.y}% - ${lift}px)) scale(${f.scale / 100})`;
}

/** What goes to the server: whole numbers, and nothing at all for the default. */
export function frameForPublish(frame) {
  if (isDefaultFrame(frame)) return null;
  const f = clampFrame(frame);
  return { x: Math.round(f.x), y: Math.round(f.y), scale: Math.round(f.scale) };
}
